import { TreeNodeData } from "@asup/tree-of-nodes";
import { eAccessRights, eHierarchy } from "functions/api/enums";
import { HierarchyChildReadDto } from "functions/api/hierarchy/HierarchyChildReadDto";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { TlfItemIndexReadDto } from "functions/api/tlfItem/TlfItemIndexReadDto";

export const ADD_HIERARCHY_NODES = "ADD_HIERARCHY_NODES";
export const ADD_SELECTION = "ADD_SELECTION";
export const REMOVE_SELECTION = "REMOVE_SELECTION";
export const SET_CORPORATE_HIERARCHY = "SET_CORPORATE_HIERARCHY";
export const SET_CURRENT_ACCESS_RIGHTS = "SET_CURRENT_ACCESS_RIGHTS";
export const SET_CURRENT_HIERARCHY = "SET_CURRENT_HIERARCHY";
export const SET_NEW_REPORT_DETAILS = "SET_NEW_REPORT_DETAILS";
export const SET_NEW_TLF_ITEM_DETAILS = "SET_NEW_TLF_ITEM_DETAILS";
export const SET_NEW_TLF_ITEM_DETAILS_VALID = "SET_NEW_TLF_ITEM_DETAILS_VALID";
export const SET_SELECTIONS = "SET_SELECTIONS";
export const UPSERT_HIERARCHY_TLF_ITEM_INDEX = "UPSERT_HIERARCHY_TLF_ITEM_INDEX";

export type HierarchyNodeData =
  (HierarchyReadDto & { url: string }) | (HierarchyChildReadDto & { url: string });

interface NewTlfItemDetails {
  azsolId?: string;
  version?: number;
  additionalInformation?: string;
  selectedDomain?: string;
  tocNumber?: string;
  sapReference?: string;
}

export interface IHierarchyBrowserContext {
  id: string;
  root: string;
  treeList: TreeNodeData<HierarchyNodeData>[];
  currentHierarchy: string;
  currentHierarchyType: eHierarchy | null;
  currentAccessRights: eAccessRights[] | null;
  showTableLinks: boolean;
  canSelect: boolean;
  selections: { id: string; azsolId: string; domain: string }[];
  hierarchyTlfItemIndex: { hierarchyId: string; tlfItemIndex: TlfItemIndexReadDto[] }[];
  newReportName?: string;
  newReportDescription?: string;
  newReportType?: string;
  newReportValid?: boolean;
  newTlfItemDetails?: NewTlfItemDetails;
  newTlfItemDetailsValid?: boolean;
}

export type HierarchyBrowserOperation =
  | typeof ADD_HIERARCHY_NODES
  | typeof ADD_SELECTION
  | typeof REMOVE_SELECTION
  | typeof SET_CORPORATE_HIERARCHY
  | typeof SET_CURRENT_ACCESS_RIGHTS
  | typeof SET_CURRENT_HIERARCHY
  | typeof SET_NEW_REPORT_DETAILS
  | typeof SET_NEW_TLF_ITEM_DETAILS
  | typeof SET_NEW_TLF_ITEM_DETAILS_VALID
  | typeof SET_SELECTIONS
  | typeof UPSERT_HIERARCHY_TLF_ITEM_INDEX;

export interface IHierarchyBrowserActionBase {
  operation: HierarchyBrowserOperation;
}

export interface IHierarchyBrowserActionAddHierarchyNodes extends IHierarchyBrowserActionBase {
  operation: typeof ADD_HIERARCHY_NODES;
  treeListData: TreeNodeData<HierarchyNodeData>[];
}

export interface IHierarchyBrowserActionAddSelection extends IHierarchyBrowserActionBase {
  operation: typeof ADD_SELECTION;
  selection: { id: string; azsolId: string; domain: string };
}

export interface IHierarchyBrowserActionRemoveSelection extends IHierarchyBrowserActionBase {
  operation: typeof REMOVE_SELECTION;
  selection: { id: string; azsolId: string; domain: string };
}

export interface IHierarchyBrowserActionSetCurrentAccessRights extends IHierarchyBrowserActionBase {
  operation: typeof SET_CURRENT_ACCESS_RIGHTS;
  accessRights: eAccessRights[] | null;
}

export interface IHierarchyBrowserActionSetCurrentHierarchy extends IHierarchyBrowserActionBase {
  operation: typeof SET_CURRENT_HIERARCHY;
  nodeId: string;
}

export interface IHierarchyBrowserActionSetCorporateHierarchy extends IHierarchyBrowserActionBase {
  operation: typeof SET_CORPORATE_HIERARCHY;
  hierarchyNode: HierarchyReadDto;
}

export interface IHierarchyBrowserActionSetNewReportName extends IHierarchyBrowserActionBase {
  operation: typeof SET_NEW_REPORT_DETAILS;
  newReportName: string;
  newReportDescription: string;
  newReportType: string;
  newReportValid: boolean;
}

export interface IHierarchyBrowserActionSetNewTlfItemDetails extends IHierarchyBrowserActionBase {
  operation: typeof SET_NEW_TLF_ITEM_DETAILS;
  tlfItemDetails: NewTlfItemDetails;
}

export interface IHierarchyBrowserActionSetNewTlfItemDetailsValid extends IHierarchyBrowserActionBase {
  operation: typeof SET_NEW_TLF_ITEM_DETAILS_VALID;
  tlfItemDetailsValid: boolean;
}

export interface IHierarchyBrowserActionSetSelections extends IHierarchyBrowserActionBase {
  operation: typeof SET_SELECTIONS;
  selections: { id: string; azsolId: string; domain: string }[];
}

export interface IHierarchyBrowserActionUpsertHierarchyTlfItemIndex extends IHierarchyBrowserActionBase {
  operation: typeof UPSERT_HIERARCHY_TLF_ITEM_INDEX;
  nodeId: string;
  tlfItemIndex: TlfItemIndexReadDto[];
}

export type IHierarchyBrowserAction =
  | IHierarchyBrowserActionAddHierarchyNodes
  | IHierarchyBrowserActionAddSelection
  | IHierarchyBrowserActionRemoveSelection
  | IHierarchyBrowserActionSetCorporateHierarchy
  | IHierarchyBrowserActionSetCurrentAccessRights
  | IHierarchyBrowserActionSetCurrentHierarchy
  | IHierarchyBrowserActionSetNewReportName
  | IHierarchyBrowserActionSetNewTlfItemDetails
  | IHierarchyBrowserActionSetNewTlfItemDetailsValid
  | IHierarchyBrowserActionSetSelections
  | IHierarchyBrowserActionUpsertHierarchyTlfItemIndex;
